import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  AllCategoriesResponse,
  BrandCategoriesResponse,
  BrandInfoResponse,
  CategoriesCarouselResponse,
  CategoriesResponse,
  CategoriesSectionSubResponse,
  CategoryFilterInput,
  CategoryInfoResponse,
  CategoryInput,
  CategoryResponse,
  CategoryTree,
  CategoryUpdateInput,
  FilterInput,
  HomeFooterCategoryResponse,
  LanguageService,
  OneCategoryResponse,
  PlatformService,
  ProductsListResponse,
  SubmitResponse
} from '@sgm-frontend/core';

@Injectable({
  providedIn: 'root'
})
export class CategoryService extends HttpService<
  // Inputs
  CategoryInput
  | CategoryFilterInput
  | FilterInput
  ,
  // Outputs
  | BrandInfoResponse[]
  | CategoryResponse
  | CategoryResponse[]
  | AllCategoriesResponse
  | CategoriesResponse
  | OneCategoryResponse
  | CategoriesCarouselResponse
  | CategoriesSectionSubResponse
  | ProductsListResponse
  | HomeFooterCategoryResponse
  | CategoryTree
  | BrandCategoriesResponse
> {
  languageService = inject(LanguageService);
  platformService = inject(PlatformService);
  private transferState = inject(TransferState);
  private document = inject(DOCUMENT);
  constructor(httpClient: HttpClient, @Inject('API_URL') apiUrl: string, @Inject('API_VERSION') apiVersion: string) {
    super(httpClient, apiUrl, apiVersion, 'category');
  }

  getAllCategories(page = 1,limit?:number): Observable<AllCategoriesResponse> {
    return this.getAllPaged('', page,'',limit) as Observable<AllCategoriesResponse>;
  }
  getAllCategoriesAdmin(page = 1,limit?:number): Observable<AllCategoriesResponse> {
    return this.getAllPaged('admin', page,'',limit) as Observable<AllCategoriesResponse>;
  }
  private static readonly TREE_KEY = makeStateKey<CategoryTree[]>('category-tree');

  exportCategories(): Observable<Blob> {
    return this.downloadFile('category/export');
  }
  getAllCategoriesTree(): Observable<CategoryTree[]> {
    // Browser hydration: reuse what the server stored in TransferState
    if (this.transferState.hasKey(CategoryService.TREE_KEY)) {
      const data = this.transferState.get(CategoryService.TREE_KEY, []);
      this.transferState.remove(CategoryService.TREE_KEY);
      return of(data);
    }

    // SSR: server.ts already baked categories into <script id="ssr-categories-data">
    // Read it, store in TransferState so browser skips the HTTP call
    if (!this.platformService.isBrowser()) {
      try {
        const scriptEl = this.document.getElementById('ssr-categories-data');
        const raw = scriptEl?.textContent;
        if (raw) {
          const data: CategoryTree[] = JSON.parse(raw);
          if (Array.isArray(data) && data.length > 0) {
            this.transferState.set(CategoryService.TREE_KEY, data);
            return of(data);
          }
        }
      } catch { /* fall through to empty */ }
      return of([]);
    }

    // Browser HTTP fallback (first load without SSR, or language change)
    return this.getAll('tree') as Observable<CategoryTree[]>;
  }
  // Admin-only variant of the tree. The public route hides departments the storefront
  // must not surface (e.g. Uncategorized), which the admin still has to be able to manage.
  getAllCategoriesTreeAdmin(): Observable<CategoryTree[]> {
    return this.getAll('admin/tree') as Observable<CategoryTree[]>;
  }
  getHomeCarouselCategories(): Observable<CategoriesCarouselResponse> {
    return this.getAll('carousel') as Observable<CategoriesCarouselResponse>;
  }

  findAllByName(text: string, page = 1): Observable<AllCategoriesResponse> {
    const filter : FilterInput = {
      page: page,
      limit: 10,
      filters: {
        and: [["category_translations.name", "ILIKE", `%${text}%`]]
      }
    }
    return this.filterAllActionPagedAdmin(filter,'admin') as Observable<AllCategoriesResponse>;
  }

  getCategoriesSectionSub(sectionId: number, page = 1): Observable<CategoriesSectionSubResponse> {
    return this.getAllPaged(`bakhoor-perfumes/sub/direct`, page) as Observable<CategoriesSectionSubResponse>;
  }

  createCategory(category: CategoryInput): Observable<SubmitResponse> {
    return this.create(category, '');
  }

//  updateCategory(category: CategoryUpdateInput): Observable<SubmitResponse> {
  // const transformedCategory: any = {
  //   ...(category.cate_name && { cate_name: category.cate_name }),
  //   ...(category.spec && { spec: category.spec })
  // };

  // return this.update(category, `${category.id}`);
// }
 updateCategory(category: CategoryInput,id:any): Observable<SubmitResponse> {
    return this.update(category, id);
  }


  deleteCategory(categoryId: number): Observable<SubmitResponse> {
    return this.delete(categoryId);
  }

  getCategoryById(id: number | string): Observable<CategoryResponse> {
    return this.getById(id) as Observable<CategoryResponse>;
  }
  getCategoryByIdAdmin(id: number | string): Observable<CategoryResponse> {
    return this.getById(id,'admin') as Observable<CategoryResponse>;
  }

  getHomeCategories(page: number): Observable<AllCategoriesResponse> {
    const input: CategoryFilterInput = {
      page,
      limit: 100,
      filters: {
        and: [['show_in_home', '=', true]],
        or: [],
      }
    };
    return this.filterAllPaged(input) as Observable<AllCategoriesResponse>;
  }
   getAllCategoriesVideos(): Observable<AllCategoriesResponse> {
    return this.getAll('have-videos') as Observable<AllCategoriesResponse>;
  }

 
   getBrandCategories(page: number, brandslug = '',limit?:number): Observable<BrandCategoriesResponse> {
    // console.log(`getVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`brand/${brandslug}`, page, '',limit) as Observable<BrandCategoriesResponse>;
  }

  getSubCategories(categoryIdOrSlug: string, nested = false): Observable<CategoryResponse[]> {
    //const suffix = nested ? '/sub?nested=true'
    return this.getBy(categoryIdOrSlug + '/sub') as Observable<CategoryResponse[]>;
  }
  getSubCategoriesDirect(categoryIdOrSlug: string, nested = false): Observable<CategoryResponse[]> {
    //const suffix = nested ? '/sub?nested=true'
    return this.getBy(categoryIdOrSlug + '/sub/direct') as Observable<CategoryResponse[]>;
  }
   getSubCategoriesAdmin(categoryIdOrSlug: string, nested = false): Observable<CategoryResponse[]> {
    //const suffix = nested ? '/sub?nested=true'
    return this.getBy('admin/'+categoryIdOrSlug + '/sub') as Observable<CategoryResponse[]>;
  }
  getSubCategoriesDirectAdmin(categoryIdOrSlug: string, nested = false): Observable<CategoryResponse[]> {
    //const suffix = nested ? '/sub?nested=true'
    return this.getBy('admin/'+categoryIdOrSlug + '/sub/direct') as Observable<CategoryResponse[]>;
  }

  getBrands(categoryIdOrSlug: string): Observable<BrandInfoResponse[]> {
    return this.getBy(categoryIdOrSlug + '/brands') as Observable<BrandInfoResponse[]>;
  }

  getVariants(page: number, categoryIdOrSlug = 'electronics',limit?:number): Observable<ProductsListResponse> {
    // console.log(`getVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`${categoryIdOrSlug}/products`, page, categoryIdOrSlug,limit) as Observable<ProductsListResponse>;
  }

  getFeaturedVariants(page: number, categoryIdOrSlug = 'electronics',limit?:number): Observable<ProductsListResponse> {
    // console.log(`getFeaturedVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`${categoryIdOrSlug}/featured`, page, categoryIdOrSlug,limit) as Observable<ProductsListResponse>;
  }

  getNewArrivalVariants(page: number, categoryIdOrSlug = 'electronics',limit?:number): Observable<ProductsListResponse> {
    // console.log(`getFeaturedVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`${categoryIdOrSlug}/new-arrival`, page, categoryIdOrSlug,limit) as Observable<ProductsListResponse>;
  }
 getProductVariants(page: number, categoryIdOrSlug = 'electronics',limit?:number): Observable<ProductsListResponse> {
    // console.log(`getFeaturedVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`${categoryIdOrSlug}/products`, page, categoryIdOrSlug,limit) as Observable<ProductsListResponse>;
  }
  getVideoVariants(page: number, categoryIdOrSlug = 'electronics'): Observable<ProductsListResponse> {
    // console.log(`getVideoVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`${categoryIdOrSlug}/videos`, page, categoryIdOrSlug) as Observable<ProductsListResponse>;
  }

  getExploreVariants(page: number, categoryIdOrSlug = 'electronics'): Observable<ProductsListResponse> {
    // console.log(`getExploreVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`${categoryIdOrSlug}/explore`, page, categoryIdOrSlug) as Observable<ProductsListResponse>;
  }

  getFooterCategories(): Observable<HomeFooterCategoryResponse> {
    return this.getBy('footer') as Observable<HomeFooterCategoryResponse>;
  }

  getBySlugs(slugs: string[]): Observable<AllCategoriesResponse> {
    const input: FilterInput = {
      page: 1,
      limit: slugs.length,
      filters: {
        and: [['slug', 'in', slugs]],
      }
    };
    return this.filterAllPaged(input) as Observable<AllCategoriesResponse>;
  }

  getByIds(ids: number[]): Observable<AllCategoriesResponse> {
    const input: FilterInput = {
      page: 1,
      limit: ids.length,
      filters: {
        and: [['id', 'in', ids]],
      }
    };
    return this.filterAllPaged(input) as Observable<AllCategoriesResponse>;
  }
    getDepartments(): Observable<AllCategoriesResponse> {
    return this.getAll('departments') as Observable<AllCategoriesResponse>;
  }

    getCategoriesbyDepartmentId(departmentId: number): Observable<CategoryResponse[]> {
    return this.getBy(`admin/${departmentId}/sub/direct?empty=true`) as Observable<CategoryResponse[]>;
  }

  getProductSpecifications(id: string): Observable<any> {
    return this.getBy(`${id}/specifications`) as Observable<any>;
  }


  // Service Data To Store Images____________--



  private data: {
    [key: string]: File | string | boolean | undefined;
  } = {};

  set(key: string, value: File | string | boolean): void {
    this.data[key] = value;
  }

  get<T>(key: string): T | undefined {
    return this.data[key] as T;
  }

  getAllServiceData(): typeof this.data {
    return this.data;
  }

  clearAllData(): void {
    this.data = {};
  }

  private showGradientColors$ = new BehaviorSubject<boolean>(false);
  private gradientColor1$ = new BehaviorSubject<string>('');
  private gradientColor2$ = new BehaviorSubject<string>('');
  private isFeatured$ = new BehaviorSubject<boolean>(false);
  private showInParentCategory$ = new BehaviorSubject<boolean>(false);
  private showParentId$ = new BehaviorSubject<any>('');
  private sortOrder$ = new BehaviorSubject<number>(1);

  get showGradientColorsChanges() {
    return this.showGradientColors$.asObservable();
  }

  get gradientColor1Changes() {
    return this.gradientColor1$.asObservable();
  }

  get gradientColor2Changes() {
    return this.gradientColor2$.asObservable();
  }

  get pariorityColorChanges() {
    return this.sortOrder$.asObservable();
  }

  get isFeaturedChanges() {
    return this.isFeatured$.asObservable();
  }

  get showInParentCategoryChanges() {
    return this.showInParentCategory$.asObservable();
  }
  get showParentIdChanges() {
    return this.showParentId$.asObservable();
  }

  // Setters
  setShowGradientColors(val: boolean) {
    this.showGradientColors$.next(val);
  }

  setGradientColor1(val: string) {
    this.gradientColor1$.next(val);
  }

  setGradientColor2(val: string) {
    this.gradientColor2$.next(val);
  }

  setSortOrder(val: number) {
    this.sortOrder$.next(val);
  }

  setIsFeatured(val: boolean) {
    this.isFeatured$.next(val);
  }

  setShowInParentCategory(val: boolean) {
    this.showInParentCategory$.next(val);
  }
  setShowParentId(val :any) {
    this.showParentId$.next(val);
  }
  clearAll(): void {
  // Clear the generic data storage
  this.data = {};

  // Reset BehaviorSubjects to their default values
  this.showGradientColors$.next(false);
  this.gradientColor1$.next('');
  this.gradientColor2$.next('');
  this.sortOrder$.next(1);
  this.isFeatured$.next(false);
  this.showInParentCategory$.next(false);
  this.showParentId$.next('');
}
 getExploreMoreBrandsVariants(page: number, brandIdOrSlug = 'apple'): Observable<ProductsListResponse> {
    // console.log(`getExploreVariants page=${page} categoryIdOrSlug=${categoryIdOrSlug}`);
    return this.getAllPaged(`brand/${brandIdOrSlug}/explore`, page) as Observable<ProductsListResponse>;
  }

}
